import { VersionedDesignModel } from '@msdk/definitions'

interface StoreConfig {
  name: string
  options?: IDBObjectStoreParameters
}

export class IndexedDb {
  private tenant: string
  private dbName: string
  private dbVersion: number
  private stores: StoreConfig[]

  constructor(tenant: string) {
    this.tenant = tenant
    this.dbName = 'SynergyMetadataCache'
    this.dbVersion = 1
    this.stores = [{ name: 'Models' }]
  }

  public async readMetadata(msName: string): Promise<VersionedDesignModel | null> {
    return await this.readFromStore<VersionedDesignModel>('Models', msName)
  }

  public async saveMetadata(msName: string, obj: VersionedDesignModel): Promise<void> {
    return await this.saveInStore<VersionedDesignModel>('Models', msName, obj)
  }

  public async deleteMetadata(msName: string): Promise<void> {
    return await this.deleteFromStore('Models', msName)
  }

  public async clearAllMetadata(): Promise<void> {
    return await this.clearStore('Models')
  }

  public async readFromStore<T>(storeName: string, key: string): Promise<T | null> {
    const db = await this.openDb()
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(storeName, 'readonly')
      const store = transaction.objectStore(storeName)
      const request = store.get(key)

      request.onsuccess = () => {
        if (request.result !== undefined) {
          resolve(request.result as T)
        } else {
          resolve(null)
        }
      }
      request.onerror = () => reject(request.error)
    })
  }

  public async saveInStore<T>(storeName: string, key: string, obj: T): Promise<void> {
    const db = await this.openDb()
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(storeName, 'readwrite')
      const store = transaction.objectStore(storeName)
      const request = store.put(obj, key)

      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  public async deleteFromStore(storeName: string, key: string): Promise<void> {
    const db = await this.openDb()
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(storeName, 'readwrite')
      const store = transaction.objectStore(storeName)
      const request = store.delete(key)

      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  public async clearStore(storeName: string): Promise<void> {
    const db = await this.openDb()
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(storeName, 'readwrite')
      const store = transaction.objectStore(storeName)
      const request = store.clear()

      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  private async openDb(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.getDbName(), this.dbVersion)
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result
        this.stores.forEach((storeConfig) => {
          if (!db.objectStoreNames.contains(storeConfig.name)) {
            db.createObjectStore(storeConfig.name, storeConfig.options || {})
          }
        })
      }

      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  private getDbName() {
    return `${this.dbName}_${this.tenant}`
  }
}
